/**
 * RecordLinksElementConfigEditor — `propertiesEditor` dla `record_links_view`
 * (right panel buildera, sub-tab „Treść"), montowany przez
 * `RecordLinksViewElement.propertiesEditor` jako komponent-dziecko (nie
 * wołany jak zwykła funkcja) — mirror `DocumentItemsColumnsCanvas` (osobny plik, montowany
 * przez `render` klocka `DocumentItemsViewElement`): hooki (`useTranslation`, `useRecordLinkTypes`)
 * żyją TU, w normalnym cyklu życia komponentu, nigdy bezpośrednio w ciele
 * `propertiesEditor` (ten sam powód co `StatCardGroupElement`/`DocumentItemsViewElement`
 * — `propertiesEditor`/`render` są wołane jak zwykłe funkcje przez
 * `LayoutElementRegistry`, nie jak komponenty).
 *
 * Kontrakt settings — patrz `RecordLinksElementConfig` w `RecordLinksSection.tsx`:
 *  - `allowAdd`/`allowUnlink` (checkbox) — pokazują/ukrywają przycisk „Dodaj
 *    powiązanie" / akcję „Odłącz" na TYM konkretnym umieszczeniu bloku.
 *  - `allowedLinkTypeIds` (multi-select typów powiązań, `forwardLabel` jako
 *    etykieta) — pusta lista = wszystkie dopuszczalne.
 *  - `allowedTargetTypes` (multi-select rodzajów encji, zlokalizowane przez
 *    `tenant.recordLinkTypes.ownerType.*` — te same klucze co słownik typów
 *    powiązań w Ustawieniach) — pusta lista = wszystkie.
 *  - `defaultLinkTypeId` (select, „— brak —" jako opcja domyślna) — lista
 *    kandydatów zawężona do `allowedLinkTypeIds`, gdy ta whitelist jest
 *    ustawiona (wybór domyślnego typu spoza własnej whitelisty nie ma sensu).
 *
 * Wartości czytane z `settings` (via `onChange` patch, mirror kontraktu
 * `LayoutElementPropertiesEditorProps` z `LayoutElementRegistry.tsx`) — ten
 * komponent NIE parsuje defensywnie (to rola `parseRecordLinksElementConfig`
 * konsumowanego na realnej stronie kartoteki) — tu settings pochodzą prosto z
 * `defaultSettings()`/poprzedniego stanu edytora w buildera, więc kształt jest
 * już poprawny.
 */

import { useTranslation } from 'react-i18next';
import type { RecordLinkOwnerType } from '@saas/shared';

import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { MultiSelect } from '@/components/MultiSelect';
import { useRecordLinkTypes } from '@/hooks/useRecordLinks';

const ALL_OWNER_TYPES_FOR_CONFIG: RecordLinkOwnerType[] = [
  'contractor',
  'employee',
  'product',
  'document',
  'task',
];

function parseStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function parseOwnerTypeArray(value: unknown): RecordLinkOwnerType[] {
  return parseStringArray(value).filter((v): v is RecordLinkOwnerType =>
    (ALL_OWNER_TYPES_FOR_CONFIG as string[]).includes(v),
  );
}

export interface RecordLinksElementConfigEditorProps {
  settings: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
}

export function RecordLinksElementConfigEditor({
  settings,
  onChange,
}: RecordLinksElementConfigEditorProps) {
  const { t } = useTranslation();
  const linkTypesQuery = useRecordLinkTypes();

  const allowAdd = settings.allowAdd !== false;
  const allowUnlink = settings.allowUnlink !== false;
  const allowedLinkTypeIds = parseStringArray(settings.allowedLinkTypeIds);
  const allowedTargetTypes = parseOwnerTypeArray(settings.allowedTargetTypes);
  const defaultLinkTypeId =
    typeof settings.defaultLinkTypeId === 'string' ? settings.defaultLinkTypeId : null;

  const linkTypes = linkTypesQuery.data ?? [];
  const linkTypeOptions = linkTypes.map((lt) => ({ value: lt.id, label: lt.forwardLabel }));
  const ownerTypeOptions = ALL_OWNER_TYPES_FOR_CONFIG.map((type) => ({
    value: type,
    label: t(`tenant.recordLinkTypes.ownerType.${type}`),
  }));

  // Kandydaci na typ domyślny zawężeni do whitelisty `allowedLinkTypeIds`, gdy ustawiona —
  // wybór domyślnego typu spoza własnej whitelisty byłby sprzeczny z konfiguracją.
  const defaultTypeCandidates =
    allowedLinkTypeIds.length > 0
      ? linkTypes.filter((lt) => allowedLinkTypeIds.includes(lt.id))
      : linkTypes;

  return (
    <div className="space-y-3" data-testid="record-links-properties-editor">
      {linkTypesQuery.isError && (
        <p
          className="text-xs text-red-600"
          data-testid="record-links-properties-editor-types-error"
        >
          {t('tenant.recordLinks.typesLoadError')}
        </p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={allowAdd}
          onChange={(e) => onChange({ allowAdd: e.target.checked })}
          className="h-4 w-4 rounded border-border-primary text-accent-600"
          data-testid="record-links-prop-allow-add"
        />
        <span className="text-text-secondary">
          {t('tenant.recordLinks.elementConfig.allowAdd')}
        </span>
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={allowUnlink}
          onChange={(e) => onChange({ allowUnlink: e.target.checked })}
          className="h-4 w-4 rounded border-border-primary text-accent-600"
          data-testid="record-links-prop-allow-unlink"
        />
        <span className="text-text-secondary">
          {t('tenant.recordLinks.elementConfig.allowUnlink')}
        </span>
      </label>

      <div>
        <span className="mb-1 block text-xs font-medium text-text-secondary">
          {t('tenant.recordLinks.elementConfig.allowedLinkTypes')}
        </span>
        <MultiSelect
          value={allowedLinkTypeIds}
          onChange={(next) => {
            onChange({
              allowedLinkTypeIds: next,
              // Odczyszczamy domyślny typ, gdy przestał być dopuszczalny po zawężeniu whitelisty.
              ...(defaultLinkTypeId && next.length > 0 && !next.includes(defaultLinkTypeId)
                ? { defaultLinkTypeId: null }
                : {}),
            });
          }}
          options={linkTypeOptions}
          placeholder={t('tenant.recordLinks.elementConfig.allowedLinkTypesPlaceholder')}
          data-testid="record-links-prop-allowed-link-types"
        />
      </div>

      <div>
        <span className="mb-1 block text-xs font-medium text-text-secondary">
          {t('tenant.recordLinks.elementConfig.allowedTargetTypes')}
        </span>
        <MultiSelect
          value={allowedTargetTypes}
          onChange={(next) => onChange({ allowedTargetTypes: next })}
          options={ownerTypeOptions}
          placeholder={t('tenant.recordLinks.elementConfig.allowedTargetTypesPlaceholder')}
          data-testid="record-links-prop-allowed-target-types"
        />
      </div>

      <FormField label={t('tenant.recordLinks.elementConfig.defaultLinkType')}>
        <Dropdown
          value={defaultLinkTypeId ?? ''}
          onChange={(value) => onChange({ defaultLinkTypeId: value || null })}
          data-testid="record-links-prop-default-link-type"
          options={[
            { value: '', label: t('tenant.recordLinks.elementConfig.defaultLinkTypeNone') },
            ...defaultTypeCandidates.map((lt) => ({ value: lt.id, label: lt.forwardLabel })),
          ]}
        />
      </FormField>
    </div>
  );
}
